import { productModelUrl } from '../productModels/productModelMap'

// What the Job Summary product viewer can show for one product, keyed by the
// same Firebase `type` string as productPhotoMap / productModelMap. The main
// photo and the .glb come from those two maps; everything else (extra photos,
// scenes, a video) is listed in PRODUCT_MEDIA below and served from
// public/product-media/. A tab with nothing behind it is not rendered, so
// adding a product's media is one entry here plus the files - no component
// changes.

const publicUrl = process.env.PUBLIC_URL || ''

export type ProductTab = 'products' | 'scene' | 'video' | 'model'

export const PRODUCT_TAB_LABELS: Readonly<Record<ProductTab, string>> = {
  model: '3D Model',
  products: 'Products',
  scene: 'Scene',
  video: 'Video',
}

/** File names under public/product-media/. */
export interface ProductMediaFiles {
  /** Extra photos, shown after the main photo. */
  readonly photos?: readonly string[]
  readonly scenes?: readonly string[]
  /** Local mp4. */
  readonly video?: string
  readonly videoPoster?: string
}

export interface ProductMedia {
  readonly modelUrl: string | undefined
  readonly photos: readonly string[]
  readonly scenes: readonly string[]
  readonly video: { readonly poster: string; readonly src: string } | undefined
}

// Empty until Myriam delivers the scene/video assets.
export const PRODUCT_MEDIA: Readonly<Record<string, ProductMediaFiles>> = {}

const mediaUrl = (file: string): string => `${publicUrl}/product-media/${file}`

/**
 * `photo` is the product's main photo URL (productPhotoMap[type]); it is passed
 * in so this file stays free of the jpg imports and runs under plain jest.
 */
export const getProductMedia = (
  type: string,
  photo: string | undefined,
  files: ProductMediaFiles | undefined = PRODUCT_MEDIA[type],
): ProductMedia => ({
  modelUrl: productModelUrl(type),
  photos: [...(photo ? [photo] : []), ...(files?.photos ?? []).map(mediaUrl)],
  scenes: (files?.scenes ?? []).map(mediaUrl),
  video: files?.video
    ? {
        poster: files.videoPoster ? mediaUrl(files.videoPoster) : '',
        src: mediaUrl(files.video),
      }
    : undefined,
})

/** Only the tabs that have content, in display order. */
export const productTabs = (media: ProductMedia): ProductTab[] => {
  const tabs: ProductTab[] = []
  if (media.photos.length) tabs.push('products')
  if (media.scenes.length) tabs.push('scene')
  if (media.video) tabs.push('video')
  if (media.modelUrl) tabs.push('model')
  return tabs
}
